import { useTranslation } from "react-i18next";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import DropDownListComponent from "@CubeComponents/form/DropdownListComponent";
import TextBoxComponent from "@CubeComponents/form/TextBoxComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { useEffect, useRef, useState } from "react";
import { showToastNotification } from "../../actions/toast";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "../../services/constants";
import css from "./Reviews.module.css";
import { statusCategories } from "@Components/PortalSetings/Configuration/PortalLevelStatus/StatusConstants";
import { useDispatch, useSelector } from "react-redux";
import {
  changeResourceStatusInReview,
  fetchBulkTransitionStatusList,
  fetchListOfStatusesAvailable,
  getStatusBtnStyle,
} from "./ReviewsHelper";
import Signature from "../Signature";
import CheckboxComponent from "@CubeComponents/form/CheckboxComponent";
import UserNameTagComponent from "../../cubeComponent/html/UserNameTagComponent";
import { SignTypeValues } from "../../cubeComponent/constants";

function ChangeFileStatusModal({
  reviewId,
  statusTo,
  closeModal,
  statusFrom,
  resources,
  type,
  requireMandatoryComment = false,
}) {
  const { t } = useTranslation();
  const user = useSelector((state) => state.userDetails);

  const dispatch = useDispatch();
  const statusToRef = useRef(null);

  const statusFromStyle = statusFrom?._id ? getStatusBtnStyle(statusFrom.category) : { backgroundColor: "#ecf3fe" };
  const defaultBackground =
    type === "standardTransition"
      ? statusCategories.find((record) => record.name === statusTo.category)
      : statusCategories.find((record) => record.name === null);

  const defaultSignatureModalData = {
    comment: "",
    statusTo: {
      ...statusTo,
      background: defaultBackground.background,
    },
    signType: SignTypeValues.DRAW_ON_SCREEN,
    signDoc: null,
  };

  const handleCheckboxClick = (e) => {
    const checked = e.target.ej2_instances[0].checked;
    setShowSignature(checked);
  };

  const [statusList, setStatusList] = useState([]);
  const [modalData, setModalData] = useState(defaultSignatureModalData);
  const [showSignature, setShowSignature] = useState(false);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [isLoadingForChangeStatus, setIsLoadingForChangeStatus] = useState(false);

  const confirmHandler = async () => {
    const { comment, statusTo, signDoc } = modalData;
    setIsLoadingForChangeStatus(true);
    const data = {
      comment,
      statusTo: statusTo._id,
      reviewId,
      resources,
      ...(showSignature && signDoc ? { signature: signDoc } : {}),
      ...(showSignature && !signDoc ? { signatureId: user.signature } : {}),
    };

    const response = await changeResourceStatusInReview(data);
    setIsLoadingForChangeStatus(false);

    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("The resource status has been updated successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      closeModal(true);
      return;
    }

    const content = response.data.errorMsg || "The resource status could not be updated";

    dispatch(showToastNotification({ content, type: TOAST_TYPE_ERROR }));
    closeModal();
  };

  const getDropdownBackground = (category) => {
    const matchedCategory = statusCategories.find((record) => record.name === category);
    const background = matchedCategory ? matchedCategory.background : BUTTON_TYPES.DROPDOWN_BACKGROUND_ROYAL_BLUE_LIST;

    return background;
  };

  const commentHandler = (e) => {
    setModalData((prev) => ({ ...prev, comment: e.value }));
  };

  const handleSignDocChange = async (e) => {
    if (e === SignTypeValues.IMPORT_PROFILE_SIGNATURE) {
      if (user.signature) {
        setIsButtonEnabled(true);
      }
    }
    setModalData((prev) => ({ ...prev, signDoc: e.base64ImageStr }));
  };

  const statusToChangeHandler = (e) => {
    const { category } = e.target.itemData;
    const background = getDropdownBackground(category);
    setModalData((prev) => ({
      ...prev,
      statusTo: { _id: e.value, background },
    }));
  };

  function getDropdownCategoryStyles(category) {
    if (category === null) {
      return { backgroundColor: "#ecf3fe" };
    }

    const matchedCategory = statusCategories.find((record) => record.name === category);
    const backgroundColor = matchedCategory ? matchedCategory.backgroundHexcode : "#0006A2";

    const style = { backgroundColor };
    return style;
  }

  function dropdownItemTemplate(args) {
    const style = getDropdownCategoryStyles(args.category);

    return (
      <div className={css.itemTemplateWrapper}>
        <span style={style} className={css.itemTemplateContainer}>
          {args.name}
        </span>
      </div>
    );
  }

  function dropdownItemTemplateBulkTransition(args) {
    const style = getDropdownCategoryStyles(args.category);

    return (
      <div className={css.itemTemplateWrapper}>
        <span className={css.itemCountTemplate}>{args.count}</span>
        <span style={style} className={css.itemTemplateContainer}>
          {args.name}
        </span>
      </div>
    );
  }

  const fetchStatusListAvailable = async () => {
    const response = await fetchListOfStatusesAvailable({
      value: { params: { reviewId, resource: resources[0] } },
    });

    if (response.data.status) {
      setStatusList(response.data.result);
      return;
    }

    dispatch(
      showToastNotification({
        content: "Internal Server Error",
        type: TOAST_TYPE_ERROR,
      }),
    );
    closeModal();
  };

  const fetchStatusListForBulkTransition = async () => {
    const response = await fetchBulkTransitionStatusList({
      value: { params: { reviewId, resources } },
    });

    if (response.data.status) {
      setStatusList(response.data.result);
      return;
    }

    dispatch(
      showToastNotification({
        content: "Internal Server Error",
        type: TOAST_TYPE_ERROR,
      }),
    );
    closeModal();
  };

  useEffect(() => {
    if (type === "standardTransition") {
      fetchStatusListAvailable();
    }

    if (type === "bulkTransition") {
      fetchStatusListForBulkTransition();
    }
  }, []);

  const getUserAvatarComponent = (user) => <UserNameTagComponent id={user.id} />;

  const header = () => <p className={css.digitalSignatureHeader}>{t("Change Status")}</p>;

  const content = () => (
    <div className={css.digitalSignatureContent}>
      <div className={css.basicDetails}>
        <div className={css.basicDetailsRow}>
          <span>{t("User")}</span>
          <div className={css.fileStatusChangeUsernameWrapper}>{getUserAvatarComponent(user)}</div>
        </div>

        <div className={css.basicDetailsRow}>
          <span>{t("Status")}</span>
          <div className={css.statusesRow}>
            <div className={css.statusWrapper}>
              <div className={css.statusFromSignatureModal} style={statusFromStyle}>
                {statusFrom?.name}
              </div>
            </div>

            <span>{t("to")}</span>

            <div className={css.statusWrapper}>
              <DropDownListComponent
                ref={statusToRef}
                dataSource={statusList}
                fields={{ text: "name", value: "_id" }}
                id="statusToDropdown"
                name="statusToDropdown"
                value={modalData.statusTo?._id}
                onChange={statusToChangeHandler}
                itemTemplate={type === "standardTransition" ? dropdownItemTemplate : dropdownItemTemplateBulkTransition}
                buttonType={modalData.statusTo.background}
              />
            </div>
          </div>
        </div>

        <div className={css.customDetailsRow}>
          {requireMandatoryComment ? <span>Comment*</span> : <span>{t("Comment")}</span>}
          <div className={css.fileStatusTextboxWrapper}>
            <TextBoxComponent
              placeholder={t("Enter Comments")}
              multiline
              rows={5}
              value={modalData.comment}
              onChange={commentHandler}
            />
          </div>
        </div>

        <div>
          <CheckboxComponent onClick={handleCheckboxClick} label="Add Signature" />
        </div>

        {showSignature && (
          <div className={`${css.customDetailsRow} gap-24`}>
            <span>{t("Sign")}</span>
            <div className={css.fileStatusTextboxWrapper}>
              <Signature
                onSuccess={(e) => handleSignDocChange(e)}
                dataSource={[
                  {
                    name: "Draw On Screen",
                    value: SignTypeValues.DRAW_ON_SCREEN,
                  },
                  {
                    name: "Attach Signed Document",
                    value: SignTypeValues.ATTACH_SIGNED_DOCUMENT,
                  },
                  {
                    name: "Add From Profile Settings",
                    value: SignTypeValues.IMPORT_PROFILE_SIGNATURE,
                  },
                ]}
                defaultSelectedSignType={SignTypeValues.DRAW_ON_SCREEN}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const footer = () => (
    <div className={css.digitalSignatureFooter}>
      <ButtonComponent
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={confirmHandler}
        disabled={
          (requireMandatoryComment && !modalData.comment.trim()) ||
          !modalData.statusTo?._id ||
          (!modalData.signDoc && showSignature && !isButtonEnabled)
        }
        loading={isLoadingForChangeStatus}
      >
        Confirm
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeModal}>
        {t("Cancel")}
      </ButtonComponent>
    </div>
  );

  return (
    <ModalStateDialogComponent
      allowModalPadding={false}
      headerTitle={header()}
      modelContent={content()}
      onCancel={closeModal}
      footerContent={footer()}
      isVisible
      zIndex={30000}
      width="680px"
    />
  );
}
ChangeFileStatusModal.displayName = "ChangeFileStatusModal";
export default ChangeFileStatusModal;
